<template>
  <li class="relative" :class="[enlargedMode ? enlargedContainerClass : tileContainerClass]">
    <div
      class="
        bg-vice-teal-600
        rounded-full
        w-10
        h-10
        content-center
        justify-center
        absolute
        top-2
        left-2
        z-10
        tooltip
      "
    >
      <MainCameraIcon class="w-full h-6" />
      <span class="tooltip-text ml-2">Main camera</span>
    </div>

    <!-- Mute -->
    <ToggleMuteStream
      v-if="sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION"
      :isStreamMuted="streamMuted"
      @stream-state-toggled="toggleStreamState"
    />

    <div
      class="h-full w-full group rounded-2xl block overflow-hidden relative"
      v-show="!sessionStore.mediaPermissionError"
    >
      <!-- Icon and trigger -->
      <div class="absolute top-1 right-1 z-20">
        <div class="p-3 relative cursor-pointer" :class="{ 'sm:p-1': !enlargedMode }">
          <div class="relative flex items-center gap-2">
            <div
              data-cy="toggle-focus"
              :class="{ 'sm:p-1': !enlargedMode }"
              class="
                bg-vice-dark-grey bg-opacity-70
                text-white
                rounded-full
                flex
                justify-center
                items-center
                p-2
                hover:bg-opacity-90
              "
            >
              <ArrowsExpandIcon
                class="h-6 w-6"
                v-if="!enlargedMode"
                :class="{ 'sm:w-5 sm:h-5': !enlargedMode }"
                @click="onEnlargeIconClick(tileId)"
              />
              <ArrowsCollapseIcon class="p-0.5" @click="onMinimizeIconClick" v-else />
            </div>
          </div>
        </div>
      </div>
      <div
        class="relative group transition-height bg-gray-800 truncate"
        v-show="!sessionStore.userIsObserver"
        :class="!isMinimized && isMinimizable ? 'h-32' : !isMinimizable ? 'h-full' : 'h-8'"
      >
        <div v-if="isMinimizable">
          <div
            class="
              h-full
              w-full
              absolute
              z-10
              transition-all
              bg-opacity-0 bg-black
              group-hover:bg-opacity-60
            "
            v-if="!isMinimized"
          >
            <ChevronDownIcon
              @click="toggleMinimizeVideo"
              class="
                absolute
                text-white
                w-6
                h-6
                opacity-0
                group-hover:opacity-100
                top-2
                right-2
                cursor-pointer
              "
            ></ChevronDownIcon>
          </div>

          <div class="w-full h-8 bg-gray-800" v-else>
            <ChevronUpIcon
              @click="toggleMinimizeVideo"
              class="absolute text-white bg-gray-800 z-50 w-8 h-8 top-0 p-1 right-0 cursor-pointer"
            ></ChevronUpIcon>
          </div>
        </div>

        <span
          v-if="streamMuted"
          class="muted-text w-full items-center justify-center rounded-xl z-10"
        >
          PAUSED
        </span>

        <div v-show="isVideoOn" class="h-full w-full relative">
          <ViceLoader v-if="!streamMuted" />

          <video
            muted
            autoplay
            playsinline
            :class="{ 'opacity-50': streamMuted }"
            class="absolute top-0 left-0 h-full w-full object-cover rounded-2xl"
            ref="videoRef"
          ></video>
        </div>

        <!-- Video off -->
        <div
          :class="[
            'flex justify-center items-center h-full w-full bg-vice-medium-teal',
            showControls && 'rounded-tl-lg rounded-bl-lg',
          ]"
          v-show="isVideoOff"
        >
          <VideoOff
            :name="sessionStore.authUser?.name"
            :class="{ 'sm:w-16 sm:h-16 sm:text-xl': isMinimizable }"
            :profile_photo_url="sessionStore.authUser?.profile_photo_url"
          />
        </div>

        <audio
          autoplay
          muted
          ref="audioRef"
          v-show="
            multiCameraDevicesStore.allCameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].isMicrophoneOn
          "
        ></audio>

        <UserLabel
          :class="{ 'text-xxs': enlargedJitsiId }"
          :localStats="localStats"
          :displayName="sessionStore.authUser?.name"
          :isMutedMicrophone="
            !multiCameraDevicesStore.allCameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].isMicrophoneOn
          "
          v-if="showTitle"
        />

        <section class="absolute bottom-0 right-0 text-left" v-show="showControls">
          <!-- TODO: adapt new designs to these buttons -->
          <button data-cy="open-external-camera-settings" class="block p-4" title="Switch Stream">
            <CogIcon class="h-6 w-6 mr-2 shadow-filter" @click="openAVSettingsModal" />
          </button>
        </section>
      </div>
    </div>
    <SpeakingIndicator :audioLevel="speakingIndicatorAudioLevel" />
  </li>
</template>

<script>
import { CogIcon } from '@heroicons/vue/solid';
import { ChevronDownIcon, ChevronUpIcon, ArrowsExpandIcon } from '@heroicons/vue/outline';
import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import VideoOff from '@/Components/Session/Grid/VideoOff.vue';
import { CAMERA_TYPES, VICE_SESSION_PHASES } from '@/Utils/vice-constants';
import ViceLoader from '@/Components/Loaders/ViceLoader.vue';
import useSessionStore from '@/Stores/useSessionStore';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import JitsiService from '@/Services/JitsiService';
import UserLabel from '@/Components/Session/Grid/UserLabel.vue';
import SpeakingIndicator from '@/Components/Session/Grid/SpeakingIndicator';
import ArrowsCollapseIcon from '@/Components/Icons/ArrowsCollapseIcon.vue';
import ToggleMuteStream from '@/Components/Inputs/ToggleMuteStream.vue';
import MainCameraIcon from '@/Components/Icons/MainCameraIcon.vue';

const Jitsi = JitsiService();

export default {
  name: 'StreamLocalPlayer',
  props: {
    index: {
      type: [Number, String],
      required: true,
    },
    showControls: {
      type: Boolean,
      default: false,
    },
    showTitle: {
      type: Boolean,
      default: false,
    },
    isMinimizable: {
      type: Boolean,
      default: false,
    },
    speakingIndicatorAudioLevel: {
      type: Number,
      default: 0,
    },
    localStats: {
      type: Number,
      default: 0,
    },
    tileId: {
      type: String,
      default: 'enlargeableStreamPlayerLocal',
    },
    gridDimension: {
      type: Object,
      required: true,
    },
    muteMedia: {
      type: Function,
    },
    jitsiId: {
      type: String,
    },
    enlargedJitsiId: {
      type: String,
    },
  },
  emits: ['toggleEnlargeMode'],
  components: {
    MainCameraIcon,
    ToggleMuteStream,
    VideoOff,
    CogIcon,
    ChevronUpIcon,
    ChevronDownIcon,
    ViceLoader,
    UserLabel,
    SpeakingIndicator,
    ArrowsExpandIcon,
    ArrowsCollapseIcon,
  },

  setup(props, { emit }) {
    const tileContainerClass = computed(
      () =>
        `col-start-${props.gridDimension[props.index].start ?? 1} col-span-${
          props.gridDimension[props.index].cols
        } row-span-${props.gridDimension[props.index].rows}`,
    );
    const enlargedContainerClass = 'col-start-2 col-span-10 row-span-8 row-start-1';
    const enlargedMode = computed(
      () => props.enlargedJitsiId === props.jitsiId && props.jitsiId !== null,
    );
    const streamMuted = ref(false);

    const isMinimized = ref(false);
    const sessionStore = useSessionStore();
    const multiCameraDevicesStore = useLocalStreamingDevicesStore();

    const videoRef = ref(null);
    const audioRef = ref(null);

    const openAVSettingsModal = () => {
      multiCameraDevicesStore.setCurrentActiveCameraType(CAMERA_TYPES.INTERNAL_WEBCAM);
      multiCameraDevicesStore.openAVSettingsModal();
    };

    const isVideoOn = computed(
      () =>
        multiCameraDevicesStore.allCameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].isCameraOn &&
        !isMinimized.value,
    );

    const isVideoOff = computed(
      () =>
        !multiCameraDevicesStore.allCameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].isCameraOn &&
        !isMinimized.value,
    );

    const toggleUserCamera = async () => {
      await multiCameraDevicesStore.toggleCamera(CAMERA_TYPES.INTERNAL_WEBCAM);
    };

    const toggleUserMicrophone = async () => {
      await multiCameraDevicesStore.toggleMicrophone(CAMERA_TYPES.INTERNAL_WEBCAM);
    };

    const toggleMinimizeVideo = () => {
      isMinimized.value = !isMinimized.value;
    };

    const onEnlargeIconClick = () => {
      emit('toggleEnlargeMode', props.jitsiId);
    };

    const onMinimizeIconClick = () => {
      emit('toggleEnlargeMode');
    };

    // Maximize the preview when the screenshare stops on the clinician side
    watch(
      () => props.isMinimizable,
      () => {
        isMinimized.value = false;
      },
    );

    watch(videoRef, (newVideoRef) => {
      if (sessionStore.userIsObserver) return;
      if (newVideoRef) {
        multiCameraDevicesStore.setVideoRef(newVideoRef, CAMERA_TYPES.INTERNAL_WEBCAM);
      }
    });

    watch(audioRef, async (newAudioRef) => {
      if (sessionStore.userIsObserver) return;
      if (newAudioRef) {
        multiCameraDevicesStore.setAudioRef(newAudioRef, CAMERA_TYPES.INTERNAL_WEBCAM);

        try {
          await Jitsi.changeAudioOutput(
            multiCameraDevicesStore.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].speakerDeviceId,
          );
          if (newAudioRef.sinkId) {
            await newAudioRef.setSinkId(
              multiCameraDevicesStore.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].speakerDeviceId,
            );
          }
        } catch (error) {
          console.error({ error });
        }
      }
    });

    onMounted(async () => {
      if (sessionStore.userIsObserver) return;
      await multiCameraDevicesStore.activateMedia(CAMERA_TYPES.INTERNAL_WEBCAM);
    });

    onUnmounted(async () => {
      if (enlargedMode.value) onMinimizeIconClick();
      if (sessionStore.userIsObserver) return;
      await multiCameraDevicesStore.endCall();
    });

    const toggleStreamState = (e) => {
      streamMuted.value = e;
      props.muteMedia(props.jitsiId, e);
    };

    return {
      videoRef,
      audioRef,
      isVideoOn,
      isVideoOff,
      isMinimized,
      streamMuted,
      enlargedMode,
      sessionStore,
      tileContainerClass,
      enlargedContainerClass,
      multiCameraDevicesStore,
      CAMERA_TYPES,
      VICE_SESSION_PHASES,
      toggleUserCamera,
      toggleStreamState,
      onEnlargeIconClick,
      onMinimizeIconClick,
      toggleMinimizeVideo,
      openAVSettingsModal,
      toggleUserMicrophone,
    };
  },
};
</script>

<style scoped>
video {
  transform: scale(-1, 1); /*For Firefox (& IE) */
  -webkit-transform: scale(-1, 1); /*for Chrome & Opera (& Safari) */
}

.text-shadow {
  text-shadow: 0 0 2px black;
}

.shadow-filter {
  filter: drop-shadow(0 0 2px black);
}

.hide {
  display: none;
}

.row-span-3 {
  grid-row-start: span 3;
  grid-row-end: span 3;
}

.col-span-3 {
  grid-column-start: span 3;
  grid-column-end: span 3;
}

.row-span-4 {
  grid-row-start: span 4;
  grid-row-end: span 4;
}

.row-span-5 {
  grid-row-start: span 5;
  grid-row-end: span 5;
}
</style>
